import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backofficeApi } from '@/lib/backoffice-api'
import type {
  StaffMember,
  StaffInvite,
  CreateStaffInviteResult,
  StaffRole,
} from '@/types/backoffice'

const KEYS = {
  staff: ['backoffice', 'staff'] as const,
  invites: ['backoffice', 'staff', 'invites'] as const,
}

export function useStaffMembers() {
  return useQuery({
    queryKey: KEYS.staff,
    queryFn: () =>
      backofficeApi.get<{ data: StaffMember[] }>('/backoffice/staff').then((r) => r.data.data),
  })
}

export function useStaffInvites() {
  return useQuery({
    queryKey: KEYS.invites,
    queryFn: () =>
      backofficeApi
        .get<{ data: StaffInvite[] }>('/backoffice/staff/invites')
        .then((r) => r.data.data),
  })
}

export function useSetStaffActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      backofficeApi
        .patch<StaffMember>(`/backoffice/staff/${id}/${active ? 'activate' : 'deactivate'}`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.staff }),
  })
}

export function useCreateStaffInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role: StaffRole }) =>
      backofficeApi.post<CreateStaffInviteResult>('/backoffice/staff/invites', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.invites }),
  })
}

export function useRevokeStaffInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      backofficeApi.delete(`/backoffice/staff/invites/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.invites }),
  })
}
